import axios from 'axios'
import hash from 'object-hash'

import { mergeDeepAll } from '../utils'
import { CompassAPIDataBase, getDataSources } from '../utils/compass'
import { getDisplayDate } from '../utils/string'
import type { BaseContext } from './'
import { commonChartOptions } from './common'

const GLOBAL_SCENARIO_CHARTS_PATH = '/api/charts/global-scenario'

export namespace GlobalScenario {
  export namespace BarPlot {
    const CONTEXT_ID = 'equity:global_scenario:bar_plot'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title: string
      subtitle: string
      referenceDate: string
      category: string
      sector: string
      timeInterval: string
      aggregateType: string
      country: string
      context: typeof CONTEXT_ID
    }

    interface BarPlotPoint {
      label: string
      value: number
    }

    interface ChartData extends CompassAPIDataBase {
      data: {
        top: BarPlotPoint[]
        bottom: BarPlotPoint[]
      }
      reference_date: string
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: GLOBAL_SCENARIO_CHARTS_PATH + '/bar-plot-data',
        method: 'POST',
        data: {
          reference_date: config.referenceDate,
          country: config.country,
          category: config.category,
          sector: config.sector,
          time_interval: config.timeInterval,
          aggregate_type: config.aggregateType,
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = [
        {
          id: `series-1`,
          type: 'column',
          name: '',
          data: data.data
            ? [
                ...data.data.top,
                { label: '', value: 0 },
                ...data.data.bottom,
              ].map((point, idx) => ({
                name: point.label,
                y: point.value,
                color: point.value >= 0 ? '#30ba09' : '#f4643d',
                ...(idx === data.data?.top.length
                  ? { dataLabels: { enabled: false } }
                  : {}),
              }))
            : [],
          dataLabels: { enabled: true, format: '{y:.2f}' },
          showInLegend: false,
          yAxis: 'primary',
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      const tooltip = { shared: true, split: undefined, valueDecimals: 2 }

      const xAxis = [
        {
          type: 'category' as const,
          plotLines: [
            data.data
              ? {
                  dashStyle: 'LongDash' as const,
                  value: data.data.top.length,
                  label: {
                    formatter: () => {
                      const top =
                        data.data && data.data.top.length > 0
                          ? `Top ${data.data.top.length}`
                          : ''
                      const bottom =
                        data.data && data.data.bottom.length > 0
                          ? `Bottom ${data.data.bottom.length}`
                          : ''
                      return `${top}<br />${bottom}`
                    },
                  },
                  zIndex: 10,
                }
              : {},
          ],
        },
      ]

      const yAxis = [
        { id: 'primary', title: { text: '' }, labels: { format: '{value}' } },
      ]

      return {
        id: Context.getChartId(config),
        title: `${config.country} - ${getDisplayDate(data.reference_date)}`,
        options: mergeDeepAll([
          creditsConfig,
          commonChartOptions,
          {
            chart: { type: 'bar' },
            series,
            stockTools: { gui: { enabled: false } },
            subtitle: {
              style: { fontSize: '14px' },
              text: config.subtitle,
            },
            tooltip,
            xAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
